/**
 * Runs `callback` once `element` scrolls near the viewport.
 *
 * Carousel libraries (Glide, Embla) read layout during their mount, which
 * forces a synchronous reflow. Deferring that work until the block is actually
 * approaching the viewport keeps it off the main thread during the initial page
 * load for blocks further down the page, which improves responsiveness (INP)
 * without changing behaviour for blocks that are visible right away.
 *
 * Falls back to running the callback immediately when IntersectionObserver is
 * unavailable.
 *
 * @param element    Element whose visibility gates the callback.
 * @param callback   Initialization to run once, when the element is near view.
 * @param rootMargin Distance from the viewport at which to trigger. Defaults to
 *                   `200px` so the block is ready slightly before it is seen.
 */
export function initWhenVisible(
	element: Element,
	callback: () => void,
	rootMargin = '200px'
): void {
	if ( typeof IntersectionObserver === 'undefined' ) {
		callback();
		return;
	}

	const observer = new IntersectionObserver(
		( entries, obs ) => {
			for ( const entry of entries ) {
				if ( entry.isIntersecting ) {
					obs.disconnect();
					callback();
					break;
				}
			}
		},
		{ rootMargin }
	);

	observer.observe( element );
}
